import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, Upload, Building2, Image as ImageIcon,
  ChevronDown, ChevronUp, Edit2, X
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

export default function AdminAlbumCompaniesPage() {
  const { token } = useAuthStore();
  const logoInputRef = useRef(null);
  const templateImageRef = useRef(null);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCompany, setExpandedCompany] = useState(null);

  // Company form
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: '', contact_info: '' });
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [savingCompany, setSavingCompany] = useState(false);

  // Template form
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateCompanyId, setTemplateCompanyId] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '' });
  const [templateImage, setTemplateImage] = useState(null);
  const [templateImagePreview, setTemplateImagePreview] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    try {
      const res = await fetch(`${API}/api/admin/album-companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setCompanies(await res.json());
    } catch {
      toast.error('Firmalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCompanyLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleTemplateImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTemplateImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setTemplateImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const openCompanyDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setCompanyForm({ name: company.name, contact_info: company.contact_info || '' });
      setLogoPreview(company.logo_url ? `${API}${company.logo_url}` : null);
    } else {
      setEditingCompany(null);
      setCompanyForm({ name: '', contact_info: '' });
      setLogoPreview(null);
    }
    setCompanyLogo(null);
    setShowCompanyDialog(true);
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim()) { toast.error('Firma adı gerekli'); return; }
    setSavingCompany(true);
    try {
      const fd = new FormData();
      fd.append('name', companyForm.name);
      fd.append('contact_info', companyForm.contact_info);
      if (companyLogo) fd.append('logo', companyLogo);

      const url = editingCompany
        ? `${API}/api/admin/album-companies/${editingCompany.id}`
        : `${API}/api/admin/album-companies`;
      const method = editingCompany ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error();
      toast.success(editingCompany ? 'Firma güncellendi' : 'Firma eklendi');
      setShowCompanyDialog(false);
      loadCompanies();
    } catch {
      toast.error('Kayıt başarısız');
    } finally {
      setSavingCompany(false);
    }
  };

  const deleteCompany = async (company) => {
    if (!window.confirm(`"${company.name}" firmasını silmek istediğinize emin misiniz? Tüm şablonları da silinecek.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/album-companies/${company.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      toast.success('Firma silindi');
      loadCompanies();
    } catch {
      toast.error('Silme başarısız');
    }
  };

  const openTemplateDialog = (companyId) => {
    setTemplateCompanyId(companyId);
    setTemplateForm({ name: '', description: '' });
    setTemplateImage(null);
    setTemplateImagePreview(null);
    setShowTemplateDialog(true);
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name.trim()) { toast.error('Şablon adı gerekli'); return; }
    if (!templateImage) { toast.error('Şablon görseli gerekli'); return; }
    setSavingTemplate(true);
    try {
      const fd = new FormData();
      fd.append('name', templateForm.name);
      fd.append('description', templateForm.description);
      fd.append('image', templateImage);

      const res = await fetch(`${API}/api/admin/album-companies/${templateCompanyId}/templates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error();
      toast.success('Şablon eklendi');
      setShowTemplateDialog(false);
      loadCompanies();
    } catch {
      toast.error('Şablon eklenemedi');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (companyId, templateId, templateName) => {
    if (!window.confirm(`"${templateName}" şablonunu silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/album-companies/${companyId}/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      toast.success('Şablon silindi');
      loadCompanies();
    } catch {
      toast.error('Silme başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-album-companies-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Albüm Firmaları</h1>
          <p className="text-muted-foreground mt-1">
            Albüm firmalarını ve modellerini yönetin
          </p>
        </div>
        <Button onClick={() => openCompanyDialog()} data-testid="add-company-btn">
          <Plus className="h-4 w-4 mr-2" />
          Yeni Firma
        </Button>
      </div>

      {companies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-medium mb-2">Henüz firma yok</h3>
            <p className="text-muted-foreground mb-4">
              Albüm firmalarını ekleyin, fotoğrafçılar çalıştıkları firmayı seçebilsin
            </p>
            <Button onClick={() => openCompanyDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              İlk Firmayı Ekle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {companies.map((company) => (
            <Card key={company.id} data-testid={`company-card-${company.id}`}>
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setExpandedCompany(expandedCompany === company.id ? null : company.id)}
              >
                <div className="flex items-center gap-4">
                  {company.logo_url ? (
                    <img
                      src={`${API}${company.logo_url}`}
                      alt={company.name}
                      className="h-12 w-12 rounded-lg object-cover border"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">{company.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {company.templates?.length || 0} model
                      {company.contact_info && ` | ${company.contact_info}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost" size="icon"
                    onClick={(e) => { e.stopPropagation(); openCompanyDialog(company); }}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    onClick={(e) => { e.stopPropagation(); deleteCompany(company); }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  {expandedCompany === company.id
                    ? <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    : <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  }
                </div>
              </div>

              {expandedCompany === company.id && (
                <CardContent className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Albüm Modelleri</h4>
                    <Button size="sm" onClick={() => openTemplateDialog(company.id)} data-testid="add-template-to-company-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Model Ekle
                    </Button>
                  </div>

                  {(company.templates?.length || 0) === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      Henüz model eklenmemiş
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {company.templates.map((template) => (
                        <div key={template.id} className="relative group rounded-lg overflow-hidden border">
                          <div className="aspect-[4/3] bg-muted">
                            {template.image_url ? (
                              <img
                                src={`${API}${template.image_url}`}
                                alt={template.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
                              </div>
                            )}
                          </div>
                          <div className="p-2">
                            <p className="text-sm font-medium truncate">{template.name}</p>
                            {template.description && (
                              <p className="text-xs text-muted-foreground truncate">{template.description}</p>
                            )}
                          </div>
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                              variant="destructive" size="sm"
                              onClick={() => deleteTemplate(company.id, template.id, template.name)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Sil
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Company Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Firmayı Düzenle' : 'Yeni Firma Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveCompany} className="space-y-4">
            <div className="space-y-2">
              <Label>Firma Adı *</Label>
              <Input
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                placeholder="Örn: Fotolife, Dilediğim"
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>İletişim Bilgisi</Label>
              <Textarea
                value={companyForm.contact_info}
                onChange={(e) => setCompanyForm({ ...companyForm, contact_info: e.target.value })}
                placeholder="Telefon, adres, web sitesi..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Firma Logosu</Label>
              <div
                className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => logoInputRef.current?.click()}
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="max-h-24 mx-auto rounded" />
                ) : (
                  <div className="py-2">
                    <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                    <p className="text-sm text-muted-foreground">Logo yükle</p>
                  </div>
                )}
              </div>
              <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoSelect} className="hidden" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCompanyDialog(false)} className="flex-1">
                İptal
              </Button>
              <Button type="submit" disabled={savingCompany} className="flex-1">
                {savingCompany ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Albüm Modeli Ekle</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveTemplate} className="space-y-4">
            <div className="space-y-2">
              <Label>Model Adı *</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="Örn: Klasik Beyaz 30x40"
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Model hakkında bilgi..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Model Görseli *</Label>
              <div
                className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => templateImageRef.current?.click()}
              >
                {templateImagePreview ? (
                  <img src={templateImagePreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                ) : (
                  <div className="py-3">
                    <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                    <p className="text-sm text-muted-foreground">Görsel yükle</p>
                  </div>
                )}
              </div>
              <input ref={templateImageRef} type="file" accept="image/*" onChange={handleTemplateImageSelect} className="hidden" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowTemplateDialog(false)} className="flex-1">
                İptal
              </Button>
              <Button type="submit" disabled={savingTemplate} className="flex-1">
                {savingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ekle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
